import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router";
import { Check, X } from "lucide-react";
import {
  DataDetail,
  DataForm,
  PageHeader,
  RelatedLinks,
  StatusPill,
  type SettingsSpec,
  type SettingsValues,
} from "@ozpanel/kit";
import {
  Eyebrow,
  Panel,
  Section,
  StateView,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useAuth,
  useToast,
} from "@ozpanel/ui";
import type { ModuleManifest } from "../modules";
import {
  ConfigOperations,
  InfoPanel,
  LookupMaps,
  OtherParameters,
  RawConfig,
  ResourcePanel,
  SetParameter,
  type ConfigCapability,
} from "./module/capabilities";
import { NotificationsTab } from "./module/NotificationsTab";
import { ServiceTab } from "./module/ServiceTab";
import { ModuleOperations } from "./module/ModuleOperations";
import { MODULE_OPS, MonitoredPanel, StatusPanel } from "./module/operations";
import { ModuleConfigTab } from "./module/ConfigTab";

// ModuleDetailScreen — the generic module console. One screen serving all 47 modules, with
// tabs appearing only for the capabilities a module actually declares: Settings for the 19
// implementing module.Settings, Config drift for the 12 implementing ConfigInspector.
//
// This is what makes the console a management tool rather than a viewer: before it, every
// module page was a read-only field dump and the only way to change a service's configuration
// was curl.

interface HealthCheck {
  name: string;
  ok: boolean;
  detail?: string;
}
interface ModuleDetail {
  manifest: ModuleManifest;
  health: { status: string; checks?: HealthCheck[] };
  has_settings?: boolean;
  has_config_diff?: boolean;
  // The standard surfaces this module ANSWERS on (handleModuleDetail in internal/server/modules.go:
  // matched against its declared routes, has_health against the generic mount), so an absent flag is
  // a fact, not a failed request. Nothing here asks a path whose flag is not set.
  has_status?: boolean;
  has_health?: boolean;
  has_raw?: boolean;
  has_metrics?: boolean;
  has_verify?: boolean;
  has_param?: boolean;
  has_reload?: boolean;
  has_reconfigure?: boolean;
  has_overrides?: boolean;
  has_settings_verify?: boolean;
  config?: ConfigCapability;
  alerts?: Parameters<typeof MonitoredPanel>[0]["alerts"];
}

// MODULE_LINKS — where a question about this module usually continues. The mail stack is four
// daemons cooperating (Postfix accepts and routes, Dovecot stores and authenticates, Rspamd
// scores and signs, the mail module owns the domains/mailboxes all three read), and a console
// that presents them as four unrelated pages makes the operator rebuild that map every time.
const MODULE_LINKS: Record<string, { label: string; to: string; hint?: string }[]> = {
  postfix: [
    { label: "Mail domains & mailboxes", to: "/modules/mail", hint: "the data its lookup tables read" },
    { label: "Dovecot", to: "/modules/dovecot", hint: "authenticates submission, stores delivery" },
    { label: "Rspamd", to: "/modules/rspamd", hint: "scores and DKIM-signs what it sends" },
    { label: "Outbound relays & routing", to: "/mail/relays", hint: "its transport & relayhost maps" },
    { label: "TLS certificates", to: "/modules/certs", hint: "the cert it presents" },
  ],
  dovecot: [
    { label: "Postfix", to: "/modules/postfix", hint: "delivers to it over LMTP" },
    { label: "Mail domains & mailboxes", to: "/modules/mail" },
  ],
  rspamd: [
    { label: "Postfix", to: "/modules/postfix", hint: "the milter it scores for" },
    { label: "Mail domains & mailboxes", to: "/modules/mail", hint: "DKIM keys are per domain" },
  ],
  mail: [
    { label: "Postfix", to: "/modules/postfix" },
    { label: "Dovecot", to: "/modules/dovecot" },
    { label: "Rspamd", to: "/modules/rspamd" },
    { label: "Domains", to: "/domains" },
    { label: "Outbound relays & routing", to: "/mail/relays" },
  ],
  caddy: [
    { label: "Websites", to: "/web", hint: "the vhosts it serves — this module's actual work" },
    { label: "TLS certificates", to: "/modules/certs" },
    { label: "App backends", to: "/apps" },
  ],

  // A module whose real screen lives somewhere else MUST say where. This page shows a manifest,
  // health and tabs; the tables themselves are at /database, and with no link there is nothing
  // on the module page to suggest they exist — which reads as "the module does nothing".
  // Compounded here by the names: `database` is ozpanel's OWN tables, `databases` is the
  // per-tenant feature, and the two sit next to each other in the module list.
  // Every one of these had NO link to the screen that does its work. A module page shows a
  // manifest, health and tabs; for these modules the thing an operator came for is on another
  // route entirely, and with nothing pointing at it the module reads as inert. That is how
  // /admin/modules/database looked: a page about the platform tables, with no tables on it and
  // no sign that /database exists.
  nftables: [
    { label: "Firewall rules", to: "/firewall", hint: "the ruleset it enforces" },
    { label: "Cluster mesh", to: "/modules/clustermesh", hint: "the traffic it must not block" },
  ],
  juicefs: [
    { label: "Shared storage", to: "/storage", hint: "every node's volumes, sessions, usage; Garage and its disks" },
    { label: "Garage", to: "/modules/garage", hint: "where every file's content lives" },
    { label: "Redis", to: "/modules/redis", hint: "the metadata engine" },
  ],
  garage: [
    { label: "Shared storage", to: "/storage", hint: "data directories (disks), resync, per-node copies" },
    { label: "JuiceFS", to: "/modules/juicefs", hint: "the filesystem it stores content for" },
  ],
  backup: [{ label: "Backups", to: "/modules/backups", hint: "schedules, runs and restores" }],
  tokens: [{ label: "Security", to: "/security", hint: "API tokens and sessions" }],
  notifications: [
    { label: "Notifications", to: "/notifications", hint: "what has actually been delivered" },
    { label: "Notification channels", to: "/modules/notify", hint: "where they are sent" },
  ],
  database: [
    { label: "Browse the platform tables", to: "/database", hint: "row counts, sizes, and a page of any table" },
    { label: "Tenant databases", to: "/modules/databases", hint: "the per-tenant feature — not this" },
    { label: "MariaDB", to: "/modules/mariadb", hint: "the server these tables live in" },
  ],
  databases: [
    { label: "Platform database", to: "/modules/database", hint: "ozpanel's own tables — not tenant data" },
    { label: "MariaDB", to: "/modules/mariadb" },
    { label: "PostgreSQL", to: "/modules/postgresql" },
  ],
  dns: [
    { label: "DNS zones", to: "/dns/zones", hint: "the zones and records it serves" },
    { label: "Domains", to: "/domains" },
    { label: "DNS hosting", to: "/modules/dns-hosting" },
  ],

  // Storage, database and cluster: the same reasoning as mail. These are not independent
  // services either — a Galera question continues in MariaDB and the arbitrator, a Gluster
  // question continues in the cluster and the mesh that carries its traffic.
  glusterfs: [
    { label: "Storage", to: "/storage", hint: "the volumes, bricks and mounts" },
    { label: "Cluster", to: "/modules/cluster", hint: "peer membership its bricks follow" },
    { label: "Cluster mesh", to: "/modules/clustermesh", hint: "the network its replication rides" },
    { label: "TLS certificates", to: "/modules/certs", hint: "stored on the shared volume" },
  ],
  mariadb: [
    { label: "Galera", to: "/modules/galera", hint: "its replication layer" },
    { label: "Galera Arbitrator", to: "/modules/garbd", hint: "the quorum tiebreaker" },
    { label: "Platform database", to: "/modules/database" },
  ],
  galera: [
    { label: "MariaDB", to: "/modules/mariadb", hint: "the server it replicates" },
    { label: "Galera Arbitrator", to: "/modules/garbd" },
    { label: "Cluster", to: "/modules/cluster" },
  ],
  garbd: [{ label: "Galera", to: "/modules/galera" }, { label: "MariaDB", to: "/modules/mariadb" }],
  redis: [
    { label: "Redis Sentinel", to: "/modules/redis-sentinel", hint: "elects its master" },
    { label: "Rspamd", to: "/modules/rspamd", hint: "stores greylist/ratelimit state here" },
  ],
  "redis-sentinel": [{ label: "Redis", to: "/modules/redis", hint: "the master it monitors" }],
  postgresql: [
    { label: "PgBouncer", to: "/modules/pgbouncer", hint: "pools connections to it" },
    { label: "PostgreSQL Failover", to: "/modules/repmgrd" },
  ],
  pgbouncer: [{ label: "PostgreSQL", to: "/modules/postgresql", hint: "the server it fronts" }],
  repmgrd: [{ label: "PostgreSQL", to: "/modules/postgresql" }],
  cluster: [
    { label: "Cluster mesh", to: "/modules/clustermesh" },
    { label: "GlusterFS", to: "/modules/glusterfs" },
    { label: "Galera", to: "/modules/galera" },
    { label: "Self-check", to: "/config-check" },
  ],
  clustermesh: [{ label: "Cluster", to: "/modules/cluster" }, { label: "Tinc VPN", to: "/modules/tinc" }],
  tinc: [{ label: "Cluster mesh", to: "/modules/clustermesh" }],
  certs: [
    { label: "Caddy", to: "/modules/caddy", hint: "issues and renews them" },
    { label: "GlusterFS", to: "/modules/glusterfs", hint: "the shared volume they are distributed on" },
    { label: "Postfix", to: "/modules/postfix", hint: "presents them for mail" },
  ],
  apps: [{ label: "Caddy", to: "/modules/caddy" }, { label: "Podman", to: "/modules/podman" }],
  podman: [{ label: "App backends", to: "/apps" }],
  backups: [{ label: "GlusterFS", to: "/modules/glusterfs" }, { label: "Tenants", to: "/tenants" }],
  usage: [{ label: "Tenants", to: "/tenants" }, { label: "Stats", to: "/modules/stats" }],
  notify: [{ label: "Notifications", to: "/notifications" }, { label: "Self-check", to: "/config-check" }],
};

const tone = (s: string) =>
  s === "healthy" ? "ok" : s === "degraded" ? "warn" : s === "critical" ? "bad" : "neutral";

export function ModuleDetailScreen() {
  const { name } = useParams<{ name: string }>();
  const { client } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const detail = useQuery({
    queryKey: ["module", name],
    queryFn: () => client.apiFetch<ModuleDetail>(`/ozpanel/modules/${name}`),
    enabled: !!name,
  });

  if (detail.isLoading || detail.isError || !detail.data) {
    return <StateView loading={detail.isLoading} error={detail.error} errorContext="this module" />;
  }

  const { manifest, health, has_settings, has_config_diff, config, alerts } = detail.data;
  const hasResources = !!config?.resources?.length;
  // Only a module backed by a systemd unit has a service to measure or control. A module with
  // several (JuiceFS: one per volume) lists them all.
  const units = manifest.units?.length ? manifest.units : manifest.unit ? [manifest.unit] : [];
  const unit = units[0];
  const Ops = MODULE_OPS[manifest.name];

  // URL-controlled tabs, so a deep-link (e.g. /modules/caddy?tab=settings from the Tunables screen)
  // opens the right tab. A requested tab this module does not have falls back to the overview rather
  // than rendering an empty panel.
  const availableTabs = new Set<string>([
    "operations",
    "actions",
    "notifications",
    "overview",
    "config",
    ...(has_settings ? ["settings"] : []),
    ...(hasResources ? ["resources"] : []),
    ...(unit ? ["service"] : []),
  ]);
  const requestedTab = searchParams.get("tab") ?? "operations";
  const activeTab = availableTabs.has(requestedTab) ? requestedTab : "operations";
  const setTab = (v: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  return (
    <div>
      <PageHeader
        crumbs={[{ label: "Modules", to: "/modules" }, { label: manifest.title }]}
        title={manifest.title}
        status={<StatusPill tone={tone(health.status)}>{health.status}</StatusPill>}
        subtitle={manifest.description}
      />

      <RelatedLinks inline links={MODULE_LINKS[manifest.name] ?? []} />

      <Tabs value={activeTab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="operations">Overview</TabsTrigger>
          {has_settings && <TabsTrigger value="settings">Settings</TabsTrigger>}
          {hasResources && <TabsTrigger value="resources">Resources</TabsTrigger>}
          {unit && <TabsTrigger value="service">Service &amp; usage</TabsTrigger>}
          {/* Every module has one: the inherited set alone guarantees it, so this tab is
              never conditional. A module with nothing of its own still shows what watches it. */}
          {/* Every module has one, for the same reason Notifications is unconditional: it is
              derived from the module's declared routes, so a module with no purpose-built screen
              still exposes everything it can do. */}
          <TabsTrigger value="actions">All operations</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="overview">Health</TabsTrigger>
          {/* Configuration, files, metrics and verify — the endpoints every module mounts
              generically. One tab rather than two conditional ones, because "raw config" and
              "drift" were two slices of the same question and neither showed metrics or verify
              at all. It renders nothing when a module implements none of them. */}
          <TabsTrigger value="config">Configuration</TabsTrigger>
        </TabsList>

        <TabsContent value="actions">
          <ModuleOperations module={manifest.name} />
        </TabsContent>

        <TabsContent value="operations">
          <div className="space-y-4">
            {Ops && <Ops />}
            <MonitoredPanel module={manifest.name} alerts={alerts ?? []} />
            {detail.data.has_status && <StatusPanel module={manifest.name} />}
            {/* THE OVERVIEW MUST NOT BE BLANK. Only about 22 of the 49 modules on a node have a
                purpose-built panel, and both of the panels above return null when they have
                nothing — no alerts, or a status endpoint the module does not implement. So for
                roughly half the modules this tab rendered NOTHING AT ALL: not "there is nothing
                here", not an error, an empty page under a heading, which reads as a broken screen
                and is indistinguishable from one.
                Where there is no purpose-built panel the module's own operations are the useful
                thing to show, and they are derived, so every module has them. */}
            {!Ops && (
              <ModuleOperations
                module={manifest.name}
                description={manifest.description}
                screen={MODULE_LINKS[manifest.name]?.[0]}
              />
            )}
          </div>
        </TabsContent>

        <TabsContent value="notifications">
          <NotificationsTab name={manifest.name} />
        </TabsContent>

        <TabsContent value="config">
          <ModuleConfigTab
            module={manifest.name}
            caps={{
              config: !!has_config_diff,
              raw: !!detail.data.has_raw,
              metrics: !!detail.data.has_metrics,
              verify: !!detail.data.has_verify,
              param: !!detail.data.has_param,
            }}
          />
        </TabsContent>

        {has_settings && (
          <TabsContent value="settings">
            <SettingsTab
              module={name!}
              hasOverrides={!!detail.data.has_overrides}
              hasCrossNodeVerify={!!detail.data.has_settings_verify}
            />
          </TabsContent>
        )}

        <TabsContent value="overview">
          <div className="grid gap-4 lg:grid-cols-2">
            <Section title="Health checks" bodyClassName="p-0">
              {health.checks?.length ? (
                <div className="divide-y divide-line">
                  {health.checks.map((c, i) => (
                    <div key={i} className="flex items-start gap-2.5 px-4 py-2.5">
                      {c.ok ? (
                        <Check size={14} className="mt-0.5 shrink-0 text-ok" />
                      ) : (
                        <X size={14} className="mt-0.5 shrink-0 text-crit" />
                      )}
                      <div className="min-w-0">
                        <div className="text-[13.5px] font-medium">{c.name}</div>
                        {c.detail && <div className="mt-0.5 text-[12px] text-muted">{c.detail}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="px-4 py-4 text-[13px] text-muted">This module reports no individual checks.</p>
              )}
            </Section>
            <Section title="Registration" collapsible defaultOpen={false} id="mod.registration" hint="manifest">
              <DataDetail
                fields={[
                  { label: "Name", value: manifest.name },
                  { label: "Version", value: manifest.version },
                  { label: "Category", value: manifest.category || "—" },
                  { label: "Owner", value: manifest.owner || "host" },
                  { label: "Unit", value: units.join(", ") || "—" },
                  { label: "Tiers", value: (manifest.tiers ?? []).join(", ") || "—" },
                ]}
              />
            </Section>
          </div>
        </TabsContent>

        {unit && (
          <TabsContent value="service">
            <div className="space-y-8">
              {units.map((u) => (
                <div key={u}>
                  {units.length > 1 && <h3 className="mb-2 font-mono text-[13px] text-muted">{u}</h3>}
                  <ServiceTab unit={u} />
                </div>
              ))}
            </div>
          </TabsContent>
        )}

        {hasResources && (
          <TabsContent value="resources">
            {config!.resources.map((r) => (
              <ResourcePanel key={r.name} resource={r} />
            ))}
            <ConfigOperations cap={config!} />
          </TabsContent>
        )}

      </Tabs>
    </div>
  );
}

// CrossNodeVerify shows whether a module's cluster-wide settings are in force on every node. It fetches
// /{module}/settings/verify — only for a module whose detail says it serves one (has_settings_verify:
// every module with cluster-wide fields, and GlusterFS, which serves its own). It used to be asked of
// every module with settings and render nothing on a 404, which a broken endpoint also produces.
// Shape: settingsVerify (internal/server/modules.go) and glusterfs's own handleVerify
// (internal/modules/glusterfs/verify_settings.go, no want, no note).
function CrossNodeVerify({ module }: { module: string }) {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["settings-verify", module],
    queryFn: () =>
      client.apiFetch<{
        nodes: string[];
        checked: number;
        consistent: boolean;
        // want: the cluster's chosen value (the generic check sends it; glusterfs's own does not).
        drift: { key: string; label: string; want?: string; values: Record<string, string> }[];
        unreachable: string[];
        // When each node last recorded its daemon's values, and how old a record may be (the generic
        // check only). The answer is built from those records, so their age is shown beside it.
        checked_at?: Record<string, string>;
        fresh_for_seconds?: number;
        note?: string;
      }>(`/${module}/settings/verify`),
    retry: false,
    refetchOnWindowFocus: false,
  });
  if (q.isError) {
    return (
      <Panel className="p-3">
        <StateView error={q.error} errorContext="the cross-node settings check" onRetry={() => void q.refetch()}>
          {null}
        </StateView>
      </Panel>
    );
  }
  const d = q.data;
  return (
    <Panel>
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-2 text-sm">
          {q.isLoading ? (
            <span className="text-muted">Checking every node…</span>
          ) : d?.consistent ? (
            <>
              <StatusPill tone="ok">Consistent</StatusPill>
              <span className="text-muted">
                All {d.checked} node{d.checked === 1 ? "" : "s"} report the same values.
              </span>
            </>
          ) : (
            <>
              <StatusPill tone="bad">Drift</StatusPill>
              <span className="text-muted">
                {d && d.drift.length > 0
                  ? `${d.drift.length} setting${d.drift.length === 1 ? "" : "s"} differ across nodes`
                  : "some nodes could not be checked"}
                .
              </span>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => void q.refetch()}
          disabled={q.isFetching}
          className="rounded border border-line px-2 py-1 text-xs text-muted hover:text-fg disabled:opacity-50"
        >
          {q.isFetching ? "Checking…" : "Re-check"}
        </button>
      </div>
      {d && d.drift.length > 0 && (
        <div className="space-y-2 border-t border-line px-4 py-3 text-sm">
          {d.drift.map((f) => (
            <div key={f.key}>
              <div className="font-medium">
                {f.label} <span className="font-mono text-xs text-muted">{f.key}</span>
                {f.want !== undefined && (
                  <span className="ml-2 text-xs font-normal text-muted">
                    cluster value: <span className="font-mono text-fg">{f.want || "(empty)"}</span>
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 font-mono text-xs">
                {Object.entries(f.values).map(([node, val]) => (
                  <span key={node} className="text-muted">
                    {node}: <span className="text-fg">{val || "(empty)"}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {d && d.unreachable.length > 0 && (
        <div className="border-t border-line px-4 py-2 text-xs text-muted">
          Not checked (unreachable): {d.unreachable.join(", ")}
        </div>
      )}
      {d?.checked_at && Object.keys(d.checked_at).length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-line px-4 py-2 text-xs text-muted">
          <span>Last recorded:</span>
          {Object.entries(d.checked_at)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([node, at]) => (
              <span key={node} className="font-mono">
                {node} {readingAge(at, q.dataUpdatedAt)}
              </span>
            ))}
          {d.fresh_for_seconds ? <span>(counted until {Math.round(d.fresh_for_seconds / 60)} min old)</span> : null}
        </div>
      )}
      {d?.note && <div className="border-t border-line px-4 py-2 text-xs text-muted">{d.note}</div>}
    </Panel>
  );
}

/** readingAge is how old a record was when the answer carrying it was fetched: "40s ago", "6m ago". */
function readingAge(at: string, fetchedAt: number): string {
  const t = Date.parse(at);
  if (Number.isNaN(t)) return "(time unknown)";
  const s = Math.max(0, Math.round((fetchedAt - t) / 1000));
  return s < 120 ? `${s}s ago` : `${Math.round(s / 60)}m ago`;
}

function SettingsTab({ module, hasOverrides, hasCrossNodeVerify }: { module: string; hasOverrides: boolean; hasCrossNodeVerify: boolean }) {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();

  const settings = useQuery({
    queryKey: ["module-settings", module],
    queryFn: () =>
      client.apiFetch<{ spec: SettingsSpec; values: SettingsValues; defaults?: Record<string, string> }>(
        `/${module}/settings`,
      ),
  });

  const save = useMutation({
    mutationFn: (changes: SettingsValues) =>
      client.apiFetch<unknown>(`/${module}/settings`, { method: "PUT", body: JSON.stringify(changes) }),
    onSuccess: (_r, changes) => {
      notify({
        kind: "success",
        title: `${Object.keys(changes).length} setting(s) applied`,
        body: "The service validated and reloaded the new configuration.",
      });
      void qc.invalidateQueries({ queryKey: ["module-settings", module] });
      void qc.invalidateQueries({ queryKey: ["module", module] });
      // The "Current state" panel above the form (spec.info_path) reads what the daemon LOADED — Caddy's
      // request limits among it — so it is re-read after a save rather than left showing the old values.
      void qc.invalidateQueries({ queryKey: ["module-info"] });
    },
    onError: (err) =>
      // WriteSettings is transactional against the daemon — a rejected change was rolled back
      // and nothing was applied. Say that, rather than leaving the operator unsure.
      notify({
        kind: "error",
        title: "The service rejected these settings",
        body: `${err instanceof Error ? err.message : String(err)} — nothing was changed.`,
      }),
  });

  const spec = settings.data?.spec;
  // The file the per-field reference cites. Read from the module's own raw_path rather than
  // assumed — only the module knows where its directives land.
  const rawFiles = useQuery({
    queryKey: ["module-raw", spec?.raw_path],
    queryFn: () => client.apiFetch<{ files: { path: string }[] }>(spec!.raw_path!),
    enabled: !!spec?.raw_path,
    retry: false,
  });
  const firstConfigFile = rawFiles.data?.files?.[0]?.path;
  // Per-service overrides, where the module publishes them. Postfix's master.cf `-o` lines
  // win over main.cf for that service, so without these the form shows a global that does not
  // describe the ports that matter.
  const overrides = useQuery({
    queryKey: ["module-overrides", module],
    queryFn: () => client.apiFetch<{ overrides: Record<string, Record<string, string>> }>(`/${module}/overrides`),
    // Only where the module serves them (postfix) — asked of every module, it 404'd on the rest.
    enabled: hasOverrides,
    retry: false,
  });
  return (
    <StateView loading={settings.isLoading} error={settings.error} errorContext="settings">
      {settings.data && spec && (
        <div className="space-y-4">
          {hasCrossNodeVerify && <CrossNodeVerify module={module} />}
          {spec.info_path && <InfoPanel path={spec.info_path} />}
          {spec.param_path && (
            <div className="flex justify-end">
              <SetParameter path={spec.param_path} module={module} />
            </div>
          )}
          <DataForm
            spec={spec}
            values={settings.data.values}
            saving={save.isPending}
            module={module}
            configFile={firstConfigFile}
            overrides={overrides.data?.overrides}
            defaults={settings.data.defaults}
            onSave={(changes) => save.mutate(changes)}
          />
          {spec.raw_path && (
            <OtherParameters
              module={module}
              rawPath={spec.raw_path}
              paramPath={spec.param_path}
              curatedKeys={new Set(spec.groups.flatMap((g) => g.fields.map((f) => f.key)))}
            />
          )}
          {spec.maps_path && (
            <LookupMaps
              crossLink={(m) =>
                // Postfix's tables are VIEWS over the mail module's data — say where to edit it
                // rather than leaving the operator to guess that a SQL query is not editable here.
                /domain|mailbox|alias|sender/.test(m.key)
                  ? { label: "Mail domains & mailboxes", to: "/modules/mail" }
                  : null
              }
            />
          )}
        </div>
      )}
    </StateView>
  );
}

/** RawTab reads the raw_path the module declares in its own spec. */

